// DocumentEditorView.jsx (updated parts to use the queue)
// -- paste this whole file if easier; it includes the queue hookup and uses enqueueCodify() --

import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box, Heading, HStack, IconButton, Image, Tooltip, useToast,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb, Text,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
} from "@chakra-ui/react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import { Extension } from "@tiptap/core";
import Paragraph from "@tiptap/extension-paragraph";
import { Plugin, TextSelection } from "prosemirror-state";
import { TableMap } from "prosemirror-tables";
import FontSize from "../extensions/FontSize";
import Navbar from "../Navbar";
import HeroImage from "../images/stamps.png";
import { CustomTableRow } from "../extensions/CustomTableRow";
import { CustomCell } from "../extensions/CustomCell";
import { CustomTableHeader } from "../extensions/CustomTableHeader";
import MenuBar from "./MenuBar";
import PlacingProcessBar from "./PlacingProcessBar";
import {
  HiOutlineArrowNarrowLeft, HiOutlineSave, HiOutlinePlus, HiOutlineMinus,
} from "react-icons/hi";
import { PageBreak } from "./PageBreak";
import AutoResizeTextarea from "../AutoResizeTextarea";
import CodifyQueue from "../utils/CodifyQueue"; // ← NEW
import ReactJson from "react-json-view";
import { useAuth } from "../AuthContext";

const DOCS_API = "https://documents.poc.mrc.ai/api/documents";
const CODIFY_API = "https://codify.poc.mrc.ai/api/codifyv3";

function safeUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "uuid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Helpers to locate table/cell & prevent "add row on last Tab"
function findTableFromSelection(selection) {
  for (let d = selection.$from.depth; d > 0; d--) {
    const node = selection.$from.node(d);
    if (node.type?.spec?.tableRole === "table") {
      return { node, pos: selection.$from.before(d) };
    }
  }
  return null;
}
function findCellFromSelection(selection) {
  for (let d = selection.$from.depth; d > 0; d--) {
    const node = selection.$from.node(d);
    const role = node.type?.spec?.tableRole;
    if (role === "cell" || role === "header_cell") {
      return { node, pos: selection.$from.before(d) };
    }
  }
  return null;
}

/** Wrap base Table, inject custom nodes, and override Tab (no auto-add row) */
const CustomTable = Table.extend({
  addExtensions() {
    return [CustomTableRow, CustomCell, CustomTableHeader];
  },
  addKeyboardShortcuts() {
    const parent = this.parent?.() ?? {};
    return {
      ...parent,
      Tab: ({ editor }) => {
        const { state } = editor;
        const tableInfo = findTableFromSelection(state.selection);
        if (!tableInfo) return parent.Tab?.({ editor }) ?? false;

        const cellInfo = findCellFromSelection(state.selection);
        if (!cellInfo) return parent.Tab?.({ editor }) ?? false;

        const map = TableMap.get(tableInfo.node);
        const tableStart = tableInfo.pos + 1;
        const rect = map.findCell(cellInfo.pos - tableStart);
        const isLastCell = rect.left === map.width - 1 && rect.top === map.height - 1;

        if (!isLastCell) return parent.Tab?.({ editor }) ?? false;

        // Move caret after table (no new row)
        const after = tableInfo.pos + tableInfo.node.nodeSize;
        let tr = state.tr;
        const nextNode = state.doc.nodeAt(after);
        if (!nextNode) {
          const para = state.schema.nodes.paragraph;
          if (para) tr = tr.insert(after, para.createAndFill());
        }
        const selPos = Math.min(after + 1, tr.doc.content.size);
        tr = tr.setSelection(TextSelection.create(tr.doc, selPos)).scrollIntoView();
        editor.view.dispatch(tr);
        return true;
      },
      // ArrowUp: place cursor at end of cell when entering from below
      ArrowUp: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;

        // Check if we're at the start of a cell
        const $pos = selection.$from;
        const cellInfo = findCellFromSelection(selection);
        if (!cellInfo) return parent.ArrowUp?.({ editor }) ?? false;

        // Check if cursor is at the very start of the cell content
        const cellStart = cellInfo.pos + 1;
        if ($pos.pos !== cellStart) return parent.ArrowUp?.({ editor }) ?? false;

        // Let default behavior move to cell above
        const result = parent.ArrowUp?.({ editor }) ?? false;

        // After moving, check if we're now in a cell and position at end
        setTimeout(() => {
          const newState = editor.state;
          const newSelection = newState.selection;
          const newCellInfo = findCellFromSelection(newSelection);

          if (newCellInfo) {
            // Position cursor at the end of the new cell
            const cellEnd = newCellInfo.pos + newCellInfo.node.nodeSize - 1;
            const tr = newState.tr.setSelection(TextSelection.create(newState.doc, cellEnd));
            editor.view.dispatch(tr);
          }
        }, 0);

        return result;
      },
    };
  },
});

/** Stamp missing tableRow.id atomically via appendTransaction */
const StableRowIds = Extension.create({
  name: "stableRowIds",
  addProsemirrorPlugins() {
    return [
      new Plugin({
        appendTransaction(_txs, _old, state) {
          let tr = state.tr;
          let changed = false;
          state.doc.descendants((node, pos) => {
            if (node.type.name === "tableRow" && !node.attrs?.id) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, id: safeUuid() });
              changed = true;
            }
            return true;
          });
          return changed ? tr : null;
        },
      }),
    ];
  },
});

export default function DocumentEditorView({
  tiptapDocument,
  contract,
  documentMeta,
  onClose,
  initialCodifiedContents = [],
}) {
  const toast = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.platformUser?.isSuperAdmin === "true";
  const [debugMode, setDebugMode] = useState(false);

  // === NEW: queue (1-at-a-time), keep across re-renders ===
  const queueRef = useRef(null);
  if (!queueRef.current) {
    queueRef.current = new CodifyQueue({ concurrency: 1, maxRetries: 2, baseDelayMs: 700, jitterMs: 300 });
  }

  const [codifiedContents, setCodifiedContents] = useState([]);
  const codifiedContentsRef = useRef(codifiedContents);
  useEffect(() => { codifiedContentsRef.current = codifiedContents; }, [codifiedContents]);

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [zoom, setZoom] = useState(1.25);
  const [panelWidth, setPanelWidth] = useState(22); // percentage
  const [isDragging, setIsDragging] = useState(false);
  const [expandedAccordionIndices, setExpandedAccordionIndices] = useState([]);

  // PlacingProcessBar state
  const [placingStage, setPlacingStage] = useState("build");
  const [placingStepCode, setPlacingStepCode] = useState("submission");
  const [participantRole, setParticipantRole] = useState("Broker");

  // Helper function to get stage display name
  const getStageDisplayName = (stage) => {
    switch (stage) {
      case "build": return "Build";
      case "quotation": return "Quotation";
      case "order": return "Order";
      case "bound": return "Bound";
      default: return "Build";
    }
  };

  const parsedDoc = React.useMemo(() => {
    if (!tiptapDocument) return null;
    try {
      return typeof tiptapDocument === "string" ? JSON.parse(tiptapDocument) : tiptapDocument;
    } catch (e) {
      console.error("Failed to parse tiptapDocument", e);
      toast({ title: "Error", description: "Invalid document format.", status: "error", duration: 4000, isClosable: true });
      return null;
    }
  }, [tiptapDocument, toast]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: false, // Disable default paragraph to use our custom one
        blockquote: {
          HTMLAttributes: {
            class: 'blockquote-content',
          },
        },
      }),
      Paragraph.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            textIndent: {
              default: null,
              parseHTML: element => element.style.textIndent || null,
              renderHTML: attributes => {
                if (!attributes.textIndent) {
                  return {};
                }
                return {
                  style: `text-indent: ${attributes.textIndent}`,
                };
              },
            },
          };
        },
      }),
      PageBreak,
      TextStyle.extend({
        addAttributes() {
          return {
            fontSize: {
              default: "1rem",
              parseHTML: el => el.style.fontSize?.replace(/['"]/g, ""),
              renderHTML: attrs => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
            },
            lineHeight: {
              default: "normal",
              parseHTML: el => el.style.lineHeight?.replace(/['"]/g, ""),
              renderHTML: attrs => (attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {}),
            },
          };
        },
      }),
      FontSize,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      CustomTable.configure({ resizable: true, withHeaderRow: true, handleWidth: 6, allowTableEditing: true }),
      StableRowIds,
      Underline,
    ],
    content: parsedDoc,
    editable: true,
  });

  // Debug (hidden)
  const [cellInfo, setCellInfo] = useState({ current: null, previous: null });

  // Ensure IDs stamped on mount
  useEffect(() => {
    if (!editor) return;
    const tr = editor.view.state.tr.setMeta("stampRowIds", true);
    editor.view.dispatch(tr);
  }, [editor]);

  // Build codified skeleton + merge persisted
  useEffect(() => {
    if (!editor) return;
    const baseByRowId = new Map();
    const order = [];
    let rowCounter = 0;

    editor.state.doc.descendants((node) => {
      if (node.type.name !== "tableRow") return true;
      const rowId = node.attrs?.id || safeUuid();
      const headingType = node.attrs?.headingType || "Unknown";

      order.push(rowId);
      baseByRowId.set(rowId, {
        id: rowId,
        collection: contract?.productCode || "mrc_v1",
        type: headingType,
        outputDataStandard: "grlc_placing",
        outputDataType: "json",
        output: null,
        status: "idle", // ← NEW: track queue status per row (idle|queued|running|done|error)
        rows: [
          {
            id: rowId,
            row: rowCounter,
            col: 1,
            segment: "tableRow",
            type: "content",
            heading: headingType,
            content: "",
            data: null,
          },
        ],
        createdDate: new Date().toISOString(),
      });
      rowCounter += 1;
      return true;
    });

    const persistedByRowId = new Map();
    if (Array.isArray(initialCodifiedContents)) {
      for (const entry of initialCodifiedContents) {
        const persistedRowId = entry?.rows?.[0]?.id || entry?.id;
        if (persistedRowId) persistedByRowId.set(persistedRowId, entry);
      }
    }

    const merged = order.map((rowId, ix) => {
      const base = baseByRowId.get(rowId);
      const persisted = persistedByRowId.get(rowId);
      if (!persisted) {
        base.rows[0].row = ix;
        return base;
      }
      return {
        ...base,
        output: persisted.output ?? base.output,
        status: persisted.status ?? base.status,
        rows: [
          {
            ...base.rows[0],
            row: ix,
            content: persisted.rows?.[0]?.content ?? base.rows[0].content,
            data: persisted.rows?.[0]?.data ?? base.rows[0].data,
          },
        ],
        createdDate: persisted.createdDate || base.createdDate,
      };
    });

    if (debugMode) console.log('[INIT] Built codifiedContents:', merged.map(m => ({ id: m.id, type: m.type, status: m.status })));
    setCodifiedContents(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, contract?.productCode, initialCodifiedContents, parsedDoc]);

  // Listen for document updates to detect new rows being added
  useEffect(() => {
    if (!editor) return;

    let updateTimer = null;

    const handleUpdate = () => {
      // Debounce to avoid rebuilding on every keystroke
      if (updateTimer) {
        clearTimeout(updateTimer);
      }

      updateTimer = setTimeout(() => {
        // Rebuild the codified contents array to include any new rows
        const currentRowIds = new Set();
        editor.state.doc.descendants((node) => {
          if (node.type.name === "tableRow") {
            currentRowIds.add(node.attrs?.id);
          }
          return true;
        });

        // Check if there are any new rows not in codifiedContents
        const existingIds = new Set(codifiedContentsRef.current.map(e => e.id));
        const hasNewRows = Array.from(currentRowIds).some(id => !existingIds.has(id));

        if (hasNewRows) {
          if (debugMode) console.log('[NEW ROW] Detected new rows, rebuilding codifiedContents');

        // Use functional update to get the LATEST state
        setCodifiedContents(prevContents => {
          // Rebuild the entire array to include new rows
          const baseByRowId = new Map();
          const order = [];
          let rowCounter = 0;
          const newRowIds = [];

          editor.state.doc.descendants((node) => {
            if (node.type.name !== "tableRow") return true;
            const rowId = node.attrs?.id || safeUuid();
            const headingType = node.attrs?.headingType || "Unknown";

            order.push(rowId);

            // Check if this row already exists in codifiedContents - use prevContents for latest state
            const existing = prevContents.find(e => e.id === rowId);

            if (existing) {
              // Preserve existing data INCLUDING latest status
              if (debugMode) console.log('[NEW ROW] Preserving existing row:', rowId, 'status:', existing.status);
              baseByRowId.set(rowId, { ...existing, rows: [{ ...existing.rows[0], row: rowCounter }] });
            } else {
              // Create new entry for new row
              newRowIds.push(rowId);
              if (debugMode) console.log('[NEW ROW] Creating entry for new row:', rowId, 'type:', headingType);

              baseByRowId.set(rowId, {
                id: rowId,
                collection: contract?.productCode || "mrc_v1",
                type: headingType,
                outputDataStandard: "grlc_placing",
                outputDataType: "json",
                output: null,
                status: "idle",
                rows: [
                  {
                    id: rowId,
                    row: rowCounter,
                    col: 1,
                    segment: "tableRow",
                    type: "content",
                    heading: headingType,
                    content: "",
                    data: null,
                  },
                ],
                createdDate: new Date().toISOString(),
              });
            }
            rowCounter += 1;
            return true;
          });

          const updated = order.map((rowId) => baseByRowId.get(rowId));
          if (debugMode) {
            console.log('[NEW ROW] Updated codifiedContents with', newRowIds.length, 'new rows');
            console.log('[NEW ROW] New rows should have default status "idle" from CustomTableRow extension');
          }
          return updated;
        });
        }
      }, 500); // 500ms debounce - only check for new rows after user stops typing
    };

    editor.on("update", handleUpdate);
    return () => {
      if (updateTimer) clearTimeout(updateTimer);
      editor.off("update", handleUpdate);
    };
  }, [editor, contract?.productCode, debugMode]);

  // Helper to update codifyStatus attribute on table rows in the editor
  const updateRowStatus = useCallback((rowId, status) => {
    if (!editor) {
      if (debugMode) console.log('[UPDATE STATUS] No editor, skipping');
      return;
    }

    const { state } = editor;
    let tr = state.tr;
    let updated = false;
    let found = false;

    state.doc.descendants((node, pos) => {
      if (node.type.name === "tableRow" && node.attrs?.id === rowId) {
        found = true;
        const currentStatus = node.attrs.codifyStatus;
        if (debugMode) console.log('[UPDATE STATUS] Found row:', rowId, 'current status:', currentStatus, 'new status:', status);

        if (currentStatus !== status) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, codifyStatus: status });
          updated = true;
          if (debugMode) console.log('[UPDATE STATUS] Updating row status');
        } else {
          if (debugMode) console.log('[UPDATE STATUS] Status already matches, no update needed');
        }
      }
      return true;
    });

    if (!found) {
      console.error('[UPDATE STATUS] Row not found:', rowId);
    }

    if (updated) {
      if (debugMode) console.log('[UPDATE STATUS] Dispatching transaction');
      editor.view.dispatch(tr);
    }
  }, [editor, debugMode]);

  // Replace in-place codify call with queued task
  const enqueueCodify = (rowId, headingType, inputText) => {
    if (debugMode) {
      console.log('[CODIFY] Enqueuing codify for rowId:', rowId, 'headingType:', headingType, 'inputText length:', inputText?.length);
      console.log('[CODIFY] Current codifiedContents IDs:', codifiedContentsRef.current.map(e => e.id));
    }

    // mark queued in UI and editor
    setCodifiedContents(prev => {
      const entry = prev.find(e => e.id === rowId);
      if (!entry) {
        console.error('[CODIFY] ERROR: No entry found for rowId:', rowId);
        return prev;
      }
      const updated = prev.map(e => (e.id === rowId ? { ...e, status: "queued" } : e));
      if (debugMode) console.log('[CODIFY] Updated entry status to queued');
      return updated;
    });
    updateRowStatus(rowId, "queued");

    queueRef.current.enqueue(rowId, async (signal) => {
      // mark running
      setCodifiedContents(prev =>
        prev.map(e => (e.id === rowId ? { ...e, status: "running" } : e))
      );
      updateRowStatus(rowId, "running");

      const payload = {
        collection: "mrc_v1",
        type: headingType || "Unknown",
        inputText,
        outputDataStandard: "grlc_placing",
        outputDataType: "json",
      };

      const res = await fetch(CODIFY_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal,
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = {}; }

      let codifiedStr = "";
      if (data?.codifiedJSON) {
        if (typeof data.codifiedJSON === "string") {
          try { codifiedStr = JSON.stringify(JSON.parse(data.codifiedJSON), null, 2); }
          catch { codifiedStr = data.codifiedJSON; }
        } else {
          codifiedStr = JSON.stringify(data.codifiedJSON, null, 2);
        }
      }

      if (!res.ok || !codifiedStr) {
        const err = new Error(data?.message || "Missing codifiedJSON");
        err.status = res.status;
        err.retriable = res.status === 429 || res.status >= 500;
        throw err;
      }

      // success: update state
      if (debugMode) console.log('[CODIFY] Success! Updating rowId:', rowId, 'with output length:', codifiedStr?.length);
      setCodifiedContents(prev => {
        const updated = prev.map(entry =>
          entry.id === rowId
            ? {
                ...entry,
                status: "done",
                output: codifiedStr,
                rows: entry.rows.map(r =>
                  r.id === rowId ? { ...r, content: inputText, data: codifiedStr } : r
                ),
              }
            : entry
        );
        if (debugMode) console.log('[CODIFY] Updated entry:', updated.find(e => e.id === rowId));
        return updated;
      });
      updateRowStatus(rowId, "done");
      return true;
    }).promise.catch(err => {
      if (err?.name === "AbortError") return;
      // mark error
      setCodifiedContents(prev =>
        prev.map(e => (e.id === rowId ? { ...e, status: "error" } : e))
      );
      updateRowStatus(rowId, "error");
      toast({
        title: "Codify Error",
        description: err?.message || "Unknown error",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    });
  };

  // Track content changes to mark rows as stale when edited after codification
  useEffect(() => {
    if (!editor) return;

    // Store last known codified content for each row
    const lastCodifiedContent = new Map();
    codifiedContentsRef.current.forEach(entry => {
      if (entry.status === "done" && entry.rows?.[0]?.content) {
        lastCodifiedContent.set(entry.id, entry.rows[0].content);
      }
    });

    let contentChangeTimer = null;

    const handleContentChange = () => {
      // Debounce content change detection to reduce performance impact
      if (contentChangeTimer) {
        clearTimeout(contentChangeTimer);
      }

      contentChangeTimer = setTimeout(() => {
        const { state } = editor;

        state.doc.descendants((node, pos) => {
          if (node.type.name !== "tableRow") return true;

          const rowId = node.attrs?.id;
          const codifyStatus = node.attrs?.codifyStatus;

          if (!rowId || codifyStatus !== "done") return true;

          // Find the content cell (second cell, role="content")
          let contentText = "";
          node.forEach((cell, offset, index) => {
            if (cell.attrs?.role === "content") {
              contentText = cell.textContent?.trim() || "";
            }
          });

          // Check if content changed from last codified version
          const lastContent = lastCodifiedContent.get(rowId);
          if (lastContent && contentText && contentText !== lastContent) {
            // Content changed - mark as stale
            setCodifiedContents(prev =>
              prev.map(e => (e.id === rowId ? { ...e, status: "stale" } : e))
            );
            updateRowStatus(rowId, "stale");
            // Update our tracking map
            lastCodifiedContent.delete(rowId);
          }

          return true;
        });
      }, 300); // 300ms debounce for content changes
    };

    editor.on("update", handleContentChange);
    return () => {
      if (contentChangeTimer) clearTimeout(contentChangeTimer);
      editor.off("update", handleContentChange);
    };
  }, [editor, updateRowStatus]);

  // Selection listener — enqueue when leaving a content cell
  useEffect(() => {
    if (!editor) return;

    const lastCellRef = { current: null };
    const lastProcessedTextRef = { current: "" };
    const cellEntryTextMap = { current: new Map() }; // Track text when entering each cell (by cellPos)
    let debounceTimer = null;

    const getTableCtx = ($pos) => {
      let rowDepth = null, cellDepth = null, cellPos = null;
      for (let d = $pos.depth; d > 0; d--) {
        const n = $pos.node(d);
        if ((n.type.name === "tableCell" || n.type.name === "tableHeader") && cellDepth === null) {
          cellDepth = d;
          cellPos = $pos.before(d); // unique position of this cell in the document
        }
        if (n.type.name === "tableRow" && rowDepth === null) rowDepth = d;
      }
      if (rowDepth == null || cellDepth == null) return null;

      const rowNode = $pos.node(rowDepth);
      const cellNode = $pos.node(cellDepth);
      const rowIndex = $pos.index(rowDepth) + 1;
      const colIndex = $pos.index(cellDepth) + 1;

      const cellAttrs = cellNode.attrs || {};
      const rowAttrs = rowNode.attrs || {};

      const headingType = cellAttrs.headingType ?? rowAttrs.headingType ?? "Unknown";
      const role = cellAttrs.role ?? "unknown";
      const rowId = rowAttrs?.id;

      return {
        rowIndex, colIndex, headingType, role,
        text: cellNode?.textContent?.trim() ?? "",
        rowId,
        cellPos, // unique identifier for this cell
      };
    };

    const handleSelectionUpdate = () => {
      if (!editor?.view?.hasFocus?.()) return;

      // Debounce to reduce frequency of updates
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(() => {
        processSelectionUpdate();
      }, 100); // 100ms debounce
    };

    const processSelectionUpdate = () => {
      if (!editor?.view?.hasFocus?.()) return;

      const { selection } = editor.state;
      const ctxNow = getTableCtx(selection.$from);

      const prevCtx = lastCellRef.current;

      // Early exit if we're still in the same cell - this prevents unnecessary processing during typing
      if (prevCtx && ctxNow && prevCtx.cellPos === ctxNow.cellPos) {
        return;
      }

      // Update debug info only when cell changes (not on every selection change)
      if (debugMode && ctxNow && (!prevCtx || prevCtx.cellPos !== ctxNow.cellPos)) {
        setCellInfo(prev => ({
          current: {
            headingType: ctxNow.headingType,
            rowIndex: ctxNow.rowIndex,
            colIndex: ctxNow.colIndex,
            rowId: ctxNow.rowId
          },
          previous: prev.current || prev.previous
        }));
      }

      // Detect when entering a new content cell - store its initial text
      const enteredContentCell = ctxNow?.role === "content" &&
        (!prevCtx || ctxNow.cellPos !== prevCtx.cellPos);

      if (enteredContentCell) {
        const entryText = ctxNow.text ?? "";
        cellEntryTextMap.current.set(ctxNow.cellPos, entryText);
      }

      lastCellRef.current = ctxNow;

      // When leaving a content cell (moving to any other cell or out of table), queue codify
      // Compare cellPos (document position) to detect actual cell changes, not just cursor moves within a cell
      const leftContentCell = prevCtx?.role === "content" &&
        (!ctxNow || ctxNow.cellPos !== prevCtx.cellPos);

      if (!leftContentCell) return;

      // IMPORTANT: Get the current text from the cell we're leaving, not the stale prevCtx.text
      // We need to fetch the actual current content from the document
      let currentText = "";
      const { state } = editor;
      state.doc.descendants((node, pos) => {
        if (pos === prevCtx.cellPos && (node.type.name === "tableCell" || node.type.name === "tableHeader")) {
          currentText = node.textContent?.trim() ?? "";
          return false; // stop searching
        }
        return true;
      });

      const cellEntryText = cellEntryTextMap.current.get(prevCtx.cellPos) ?? "";

      // Skip if no text, too short, or unchanged since entering the cell
      if (!currentText || currentText.length <= 3 || currentText === cellEntryText) {
        return;
      }

      lastProcessedTextRef.current = currentText;
      cellEntryTextMap.current.set(prevCtx.cellPos, currentText); // Update for next time

      const rowId = prevCtx.rowId;
      const headingType = prevCtx.headingType || "Unknown";

      if (!rowId) {
        return;
      }

      enqueueCodify(rowId, headingType, currentText);
    };

    editor.on("selectionUpdate", handleSelectionUpdate);
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      editor.off("selectionUpdate", handleSelectionUpdate);
    };
  }, [editor, toast, debugMode]);

  const handleSave = async () => {
    if (!editor) return;
    const repoId = documentMeta?.documentRepositoryId;
    if (!repoId) {
      toast({ title: "No linked document", description: "Link a document in the repository before saving.", status: "warning", duration: 3500, isClosable: true });
      return;
    }
    const payload = {
      definitionId: documentMeta?.definitionId ?? "",
      name: documentMeta?.name || "Untitled Document",
      content: JSON.stringify(editor.getJSON()),
      codifiedContents,
      createdDate: documentMeta?.createdDate ?? new Date().toISOString(),
    };
    try {
      const res = await fetch(`${DOCS_API}/${repoId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload, null, 2),
      });
      if (!res.ok) throw new Error((await res.text().catch(() => "")) || "Failed to save document to repository");
      toast({ title: "Document Saved", status: "success", duration: 2500, isClosable: true });
    } catch (err) {
      toast({ title: "Save Failed", description: err?.message || "Unknown error", status: "error", duration: 4000, isClosable: true });
    }
  };

  const handleClose = () => {
    if (debugMode) console.log("[DocumentEditorView] handleClose called, onClose:", onClose);
    if (onClose) {
      if (debugMode) console.log("[DocumentEditorView] Calling onClose callback");
      onClose();
    } else {
      if (debugMode) console.log("[DocumentEditorView] No onClose callback, using window.history.back()");
      window.history.back();
    }
  };

  // Function to open codified panel and scroll to specific row
  const handleStatusDotClick = useCallback((rowId) => {
    if (debugMode) {
      console.log("[DOT CLICK] rowId:", rowId);
    }

    // Open the drawer if not already open
    setDrawerOpen(prev => {
      const wasOpen = prev;
      if (debugMode) {
        console.log("[DOT CLICK] drawer wasOpen:", wasOpen);
      }

      // Find the index of this row in codifiedContents
      const index = codifiedContents.findIndex(c => c.id === rowId);
      if (debugMode) {
        console.log("[DOT CLICK] found index:", index, "in", codifiedContents.length, "items");
      }
      if (index === -1) return prev;

      // Expand this accordion item
      setExpandedAccordionIndices(prevIndices => {
        if (prevIndices.includes(index)) {
          if (debugMode) {
            console.log("[DOT CLICK] index already expanded");
          }
          return prevIndices;
        }
        const newIndices = [...prevIndices, index];
        if (debugMode) {
          console.log("[DOT CLICK] expanding accordion index:", index, "new indices:", newIndices);
        }
        return newIndices;
      });

      // Scroll to the item after a short delay (to allow drawer to open)
      setTimeout(() => {
        const accordionItem = document.querySelector(`[data-codify-id="${rowId}"]`);
        if (debugMode) {
          console.log("[DOT CLICK] accordion item found:", !!accordionItem);
        }
        if (accordionItem) {
          accordionItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, wasOpen ? 0 : 300);

      return true; // open the drawer
    });
  }, [codifiedContents, debugMode]);

  // Attach click handlers to status dots
  useEffect(() => {
    if (!editor) return;

    const handleClick = (event) => {
      // Check if click was on a status dot (the ::after pseudo-element area)
      // Target either the 2nd cell or a merged cell with data-role="content"
      const cell = event.target.closest('td:nth-child(2), th:nth-child(2), td[data-role="content"], th[data-role="content"]');
      if (!cell) return;

      // Check if click was in the right area (where the dot is positioned)
      const rect = cell.getBoundingClientRect();
      const clickX = event.clientX;
      const clickY = event.clientY;

      // Dot is positioned at right: 8px, top: 50%, width/height: 8px
      // Expand click area to make it easier to hit
      const dotLeft = rect.right - 24; // larger click area
      const dotTop = rect.top + (rect.height / 2) - 12;
      const dotBottom = rect.top + (rect.height / 2) + 12;

      if (clickX >= dotLeft && clickX <= rect.right &&
          clickY >= dotTop && clickY <= dotBottom) {
        // Find the closest tr element with data-id
        const row = event.target.closest('tr[data-id]');
        if (!row) {
          if (debugMode) {
            console.log("[DOT CLICK] Click detected in dot area but no row found");
          }
          return;
        }

        const rowId = row.getAttribute('data-id');
        if (rowId) {
          if (debugMode) {
            console.log("[DOT CLICK] Click detected on dot for rowId:", rowId);
          }
          event.preventDefault();
          event.stopPropagation();
          handleStatusDotClick(rowId);
        }
      }
    };

    // Use event delegation on the editor container
    const editorEl = editor.view.dom;
    editorEl.addEventListener('click', handleClick, true); // use capture phase

    return () => {
      editorEl.removeEventListener('click', handleClick, true);
    };
  }, [editor, handleStatusDotClick]);

  // Drag handlers for resizable panel
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const containerWidth = window.innerWidth;
      const newWidth = ((containerWidth - e.clientX) / containerWidth) * 100;
      // Constrain between 15% and 50%
      setPanelWidth(Math.min(Math.max(newWidth, 15), 50));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <Box height="100vh" display="flex" flexDirection="column" overflow="hidden">
      <Navbar />

      {/* Background Image */}
      <Box position="fixed" width="100%" height="100%" top={0} left={0} zIndex={0}>
        <Image src={HeroImage} alt="Hero Watermark" width="100%" height="100%" objectFit="cover" opacity={0.35} filter="blur(1px)" />
      </Box>

      {/* Main Content Container */}
      <Box position="relative" zIndex={1} p={10} flex="1" display="flex" flexDirection="column" overflow="hidden">

        {/* Title Bar - Fixed */}
        <Box borderWidth="1px" borderRadius="md" p={3} mb={2} borderColor="gray.500" backgroundColor="whiteAlpha.900" flexShrink={0}>
          <HStack spacing={6}>
            <Tooltip label="Back to the Product Dashboard">
              <IconButton icon={<HiOutlineArrowNarrowLeft />} size="md" onClick={handleClose} />
            </Tooltip>
            <Heading size="lg">{documentMeta?.name || contract?.name || "Document"}</Heading>
            <Tooltip label="Save">
              <IconButton icon={<HiOutlineSave />} size="lg" onClick={handleSave} />
            </Tooltip>
            <Box fontSize="sm" color="gray.600" ml="auto">Codify Queue: {queueRef.current.size}</Box>
          </HStack>
        </Box>

        {/* Debug Info - Fixed */}
        {debugMode && (
          <Box bg="yellow.100" border="1px solid" borderColor="yellow.400" p={2} mb={2} borderRadius="md" flexShrink={0}>
            <Text as="strong" fontSize="xs">Current Cell:</Text>{" "}
            {cellInfo.current ? `${cellInfo.current.headingType || "N/A"} | Row: ${cellInfo.current.rowIndex ?? "?"} | Col: ${cellInfo.current.colIndex ?? "?"} | RowID: ${cellInfo.current.rowId ? cellInfo.current.rowId.substring(0, 8) + '...' : 'N/A'}` : "None"}
            <br />
            <Text as="strong" fontSize="xs">Previous Cell:</Text>{" "}
            {cellInfo.previous ? `${cellInfo.previous.headingType || "N/A"} | Row: ${cellInfo.previous.rowIndex ?? "?"} | Col: ${cellInfo.previous.colIndex ?? "?"} | RowID: ${cellInfo.previous.rowId ? cellInfo.previous.rowId.substring(0, 8) + '...' : 'N/A'}` : "None"}
          </Box>
        )}

        {/* Editor Container */}
        <Box
          className="mrc-editor"
          display="flex"
          flexDirection="column"
          borderWidth="1px"
          borderRadius="md"
          borderColor="gray.500"
          backgroundColor="whiteAlpha.900"
          flex="1"
          overflow="hidden"
        >
          {/* PlacingProcessBar - Fixed */}
          <PlacingProcessBar
            currentStage={placingStage}
            currentStepCode={placingStepCode}
            participantRole={participantRole}
            onStepChange={(stage, stepCode) => {
              setPlacingStage(stage);
              setPlacingStepCode(stepCode);
            }}
            onStageChange={(stage, stepCode) => {
              setPlacingStage(stage);
              setPlacingStepCode(stepCode);
              toast({
                title: `Switched to ${getStageDisplayName(stage)} Stage`,
                status: "info",
                duration: 2000,
                isClosable: true
              });
            }}
          />

          {/* MenuBar - Fixed */}
          <Box borderBottom="1px" borderColor="gray.300" p={2} backgroundColor="whiteAlpha.900" flexShrink={0}>
            <MenuBar
              editor={editor}
              onSave={handleSave}
              onClose={handleClose}
              onShowCodified={() => setDrawerOpen(!isDrawerOpen)}
              debugMode={debugMode}
              onToggleDebug={() => setDebugMode(!debugMode)}
              isSuperAdmin={isSuperAdmin}
            />
          </Box>

          {/* Scrollable Content Area */}
          <HStack flex="1" spacing={0} alignItems="stretch" overflow="hidden">
            <Box
              flex="1"
              overflowY="auto"
              overflowX="hidden"
              w={isDrawerOpen ? `${100 - panelWidth}%` : "100%"}
            >
              <div className="editor-wrapper" style={{ zoom }}>
                <div className="editor-page">
                  <EditorContent editor={editor} />
                </div>
              </div>
            </Box>

            {isDrawerOpen && (
              <>
                <Box
                  w="4px"
                  h="100%"
                  bg="gray.300"
                  cursor="col-resize"
                  _hover={{ bg: "blue.400" }}
                  transition="background-color 0.2s"
                  onMouseDown={handleMouseDown}
                  userSelect="none"
                />
                <Box
                  w={`${panelWidth}%`}
                  p={2}
                  bg="white"
                  display="flex"
                  flexDirection="column"
                  overflow="hidden"
                  zIndex={10}
                  borderLeft="1px solid"
                  borderColor="gray.300"
                >
                  <HStack justify="space-between" mb={2} flexShrink={0}>
                    <Heading size="sm">Codified Data</Heading>
                    <IconButton icon={<HiOutlineArrowNarrowLeft />} aria-label="Close Codified" onClick={() => setDrawerOpen(false)} size="sm" />
                  </HStack>

                  <Box flex="1" overflowY="auto" overflowX="hidden">
                    <Accordion allowMultiple index={expandedAccordionIndices} onChange={setExpandedAccordionIndices}>
                      {codifiedContents.map((c, idx) => (
                        <AccordionItem key={c.id} border="1px solid" borderColor="gray.200" borderRadius="md" mb={2} data-codify-id={c.id}>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <HStack justify="space-between">
                                <Text fontWeight="semibold" fontSize="sm">{c.type}</Text>
                                <Text fontSize="xs" color={
                                  c.status === "running" ? "blue.600" :
                                  c.status === "queued" ? "orange.600" :
                                  c.status === "error" ? "red.600" :
                                  c.status === "done" ? "green.600" : "gray.600"
                                }>
                                  {c.status}
                                </Text>
                              </HStack>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel pb={2} fontSize="xs">
                            {c.output ? (
                              (() => {
                                try {
                                  const parsed = JSON.parse(c.output);
                                  return (
                                    <ReactJson
                                      src={parsed}
                                      theme="rjv-default"
                                      collapsed={false}
                                      displayDataTypes={false}
                                      displayObjectSize={false}
                                      enableClipboard={true}
                                      name={false}
                                      style={{ fontSize: "11px", backgroundColor: "white" }}
                                    />
                                  );
                                } catch {
                                  return <Text fontSize="xs" color="gray.600">{c.output}</Text>;
                                }
                              })()
                            ) : (
                              <Text fontSize="xs" color="gray.500">No output yet</Text>
                            )}
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </Box>
                </Box>
              </>
            )}
          </HStack>

          {/* Zoom Controls - Fixed at bottom of editor */}
          <Box p={2} borderTop="1px" borderColor="gray.300" backgroundColor="whiteAlpha.900" flexShrink={0}>
            <HStack spacing={4} justify="center">
              <Tooltip label="Zoom Out">
                <IconButton icon={<HiOutlineMinus />} size="sm" onClick={() => setZoom((prev) => Math.max(prev - 0.1, 0.5))} />
              </Tooltip>
              <Slider aria-label="Zoom Slider" value={zoom * 100} min={50} max={200} onChange={(val) => setZoom(val / 100)} width="200px">
                <SliderTrack><SliderFilledTrack /></SliderTrack>
                <SliderThumb />
              </Slider>
              <Text fontSize="sm" minW="50px" textAlign="center">{Math.round(zoom * 100)}%</Text>
              <Tooltip label="Zoom In">
                <IconButton icon={<HiOutlinePlus />} size="sm" onClick={() => setZoom((prev) => Math.min(prev + 0.1, 2))} />
              </Tooltip>
            </HStack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
